import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TextTab, Tooltip } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';

import { TabType } from '@domains/pdp-common';
import { stickyTabRefAtom } from '@domains/pdp-common';

import { useSizeTabABT } from '../../size-info/abt';
import { ProductTab } from '../hooks/useProductTabList';

interface Props {
  isSizeTabTooltipShown: boolean;
  isZigzagApp: boolean;
  activeTabType: TabType;
  tabList: ProductTab[];
  tabComponentList: Record<TabType, JSX.Element>;
  changeTab: (tab_type: TabType) => void;
  hideSizeTabTooltip: () => void;
}

export const Tabs: React.FC<React.PropsWithChildren<Props>> = ({
  isSizeTabTooltipShown,
  isZigzagApp,
  activeTabType,
  tabList,
  tabComponentList,
  changeTab,
  hideSizeTabTooltip,
}) => {
  const setRef = useSetAtom(stickyTabRefAtom);
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: '50%' });
  const [isMounted, setIsMounted] = useState(false);
  const { isTestGroup } = useSizeTabABT();

  const tooltipContent = isTestGroup ? '내 옷과 사이즈를 비교해 보세요' : '실측 사이즈를 확인할 수 있어요';

  // 사이즈 탭 존재 여부 메모이제이션
  const hasSizeTab = useMemo(() => tabList.some((tab) => tab.tabType === TabType.SIZE_INFO), [tabList]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const refCallback = useCallback(
    (node: HTMLElement | null) => {
      setRef({ current: node });
      (tabContainerRef as React.MutableRefObject<HTMLElement | null>).current = node;
    },
    [setRef],
  );

  // 탭 위치를 계산하는 함수 - 실제 DOM 요소 기준으로 정확한 위치 계산
  const calculateTooltipPosition = useCallback(() => {
    if (!tabContainerRef.current) return;

    const sizeTabIndex = tabList.findIndex((tab) => tab.tabType === TabType.SIZE_INFO);
    if (sizeTabIndex === -1) return;

    // TextTab 내부의 버튼들을 찾기
    const tabButtons = tabContainerRef.current.querySelectorAll('button');
    const sizeTabButton = tabButtons[sizeTabIndex];

    if (!sizeTabButton) return;

    // 사이즈 탭 버튼과 컨테이너의 위치 정보 가져오기
    const buttonRect = sizeTabButton.getBoundingClientRect();
    const containerRect = tabContainerRef.current.getBoundingClientRect();

    // 버튼 중앙의 상대 좌표 계산
    const buttonCenterX = buttonRect.left + buttonRect.width / 2;
    const relativeLeft = buttonCenterX - containerRect.left;

    setTooltipPosition({ left: `${relativeLeft}px` });
  }, [tabList]);

  // 탭 데이터가 준비되고 툴팁 표시 시 위치 계산
  // tabList 의존성으로 동적 탭 생성 후 정확한 위치 계산 보장
  useEffect(() => {
    if (!isMounted || !hasSizeTab || !isSizeTabTooltipShown) {
      return;
    }

    // DOM 렌더링 완료 후 위치 계산
    requestAnimationFrame(() => {
      calculateTooltipPosition();
    });
  }, [isMounted, hasSizeTab, isSizeTabTooltipShown, tabList, calculateTooltipPosition]);

  // ResizeObserver를 사용하여 탭 컨테이너 크기 변경 감지
  useEffect(() => {
    if (!tabContainerRef.current || !isSizeTabTooltipShown) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateTooltipPosition();
    });

    resizeObserver.observe(tabContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isSizeTabTooltipShown, calculateTooltipPosition]);

  return (
    <>
      <HeadContainer className='tab-head' isZigzagApp={isZigzagApp} ref={refCallback}>
        {isMounted && (
          <TextTab
            key={activeTabType}
            fullWidth
            defaultValue={activeTabType}
            impressionMode='once'
            impressionObserverOptions={{
              rootMargin: '0px',
              threshold: 0.5,
            }}
            items={tabList.map(({ tabType, label, secondaryText, hasNotification }) => ({
              value: tabType,
              label,
              secondaryText,
              hasNotification,
            }))}
            onChange={(value) => changeTab(value)}
            size='medium'
          />
        )}
        {isSizeTabTooltipShown && (
          <SizeTabTooltip
            opened
            placement='bottom'
            openTrigger='none'
            scrollClose={false}
            outsideClickClose={false}
            hasCloseButton
            content={tooltipContent}
            onClose={hideSizeTabTooltip}
          >
            <div style={{ position: 'relative', left: tooltipPosition.left, width: 1 }} />
          </SizeTabTooltip>
        )}
      </HeadContainer>
      <TabBody overflow={activeTabType === TabType.REVIEW ? 'unset' : 'hidden'}>
        {tabComponentList[activeTabType]}
      </TabBody>
    </>
  );
};

const HeadContainer = styled.div<{ isZigzagApp: boolean }>`
  position: sticky;
  top: ${({ isZigzagApp }) => (isZigzagApp ? 0 : '44px')};
  z-index: 200;
  background: #fff;
`;

const SizeTabTooltip = styled(Tooltip)`
  position: absolute;
  top: 100%;
  transform: translateX(-50%);
  z-index: 300;
  margin-top: 8px;
`;

const TabBody = styled.div<{ overflow: 'unset' | 'hidden' }>`
  --top-navigation-height: 52px;
  min-height: calc(100vh - var(--top-navigation-height));
  overflow: ${({ overflow }) => overflow ?? 'hidden'};

  .line-advertisement-inventory ~ .related-stylist-list {
    border-top: 1px solid ${vars.v3Color.gray100};
  }

  .related-stylist-list {
    margin-bottom: 40px;
  }

  .line-advertisement-inventory ~ .related-recommend-list {
    border-top: 1px solid ${vars.v3Color.gray100};
  }

  .related-stylist-list ~ .related-recommend-list {
    border-top: none;
  }

  .line-advertisement-inventory ~ .infinite-recommend-list,
  .related-stylist-list ~ .infinite-recommend-list,
  .related-recommend-list ~ .infinite-recommend-list {
    border-top: 1px solid ${vars.v3Color.gray100};
  }
`;
